import { useCallback, useEffect, useState } from 'react';

interface VideoManagerState {
  currentlyPlaying: null | string;
  managerInitiatedEvents: Set<string>;
}

interface VideoRegistration {
  cleanup: () => void;
  element: HTMLVideoElement;
}

interface PauseAllVideosOptions {
  excludeVideoId?: string;
}

// Global state - single source of truth
let globalState: VideoManagerState = {
  currentlyPlaying: null,
  managerInitiatedEvents: new Set(),
};

// Video registry for managing all video elements
const videoRegistry = new Map<string, VideoRegistration>();

// Subscribers for state changes
const subscribers = new Set<() => void>();

// Notify all subscribers of state changes
const notifySubscribers = () => {
  if (typeof window !== 'undefined') {
    subscribers.forEach(callback => callback());
  }
};

// Core video management operations
const VideoManager = {
  // Get currently playing video ID
  getCurrentlyPlaying(): null | string {
    return globalState.currentlyPlaying;
  },

  // Check if a video is currently playing
  isVideoPlaying(videoId: string): boolean {
    return globalState.currentlyPlaying === videoId;
  },

  // Pause all videos and clear state
  async pauseAllVideos(options?: PauseAllVideosOptions): Promise<void> {
    const excludeVideoId = options?.excludeVideoId;
    
    // Mark all pause events as manager-initiated (except excluded video)
    videoRegistry.forEach((registration, videoId) => {
      if (videoId !== excludeVideoId && !registration.element.paused) {
        globalState.managerInitiatedEvents.add(`pause-${videoId}`);
        registration.element.pause();
      }
    });

    // Clear global state only if no video is being excluded, 
    // otherwise it will be set by switchToVideo
    if (!excludeVideoId) {
      globalState.currentlyPlaying = null;
    }
  },

  // Pause a specific video
  pauseVideo(videoId: string): void {
    if (globalState.currentlyPlaying === videoId) {
      globalState.currentlyPlaying = null;
      notifySubscribers();
    }

    const registration = videoRegistry.get(videoId);
    if (registration && !registration.element.paused) {
      globalState.managerInitiatedEvents.add(`pause-${videoId}`);
      registration.element.pause();
    }
  },

  // Register a video element
  registerVideo(videoId: string, element: HTMLVideoElement): () => void {
    // Create event handlers for this video
    const handlePlay = () => {
      const eventKey = `play-${videoId}`;

      // If this is a manager-initiated play, just remove the flag
      if (globalState.managerInitiatedEvents.has(eventKey)) {
        globalState.managerInitiatedEvents.delete(eventKey);
        return;
      }

      // User-initiated play - sync state
      if (globalState.currentlyPlaying !== videoId) {
        // User manually played a video - pause all others
        VideoManager.switchToVideo(videoId);
      }
    };

    const handlePause = () => {
      const eventKey = `pause-${videoId}`;

      // If this is a manager-initiated pause, just remove the flag
      if (globalState.managerInitiatedEvents.has(eventKey)) {
        globalState.managerInitiatedEvents.delete(eventKey);
        return;
      }

      // User-initiated pause - sync state
      if (globalState.currentlyPlaying === videoId) {
        globalState.currentlyPlaying = null;
        notifySubscribers();
      }
    };

    const handleEnded = () => {
      // Always handle ended events (clear state)
      if (globalState.currentlyPlaying === videoId) {
        globalState.currentlyPlaying = null;
        notifySubscribers();
      }
    };

    // Add event listeners
    element.addEventListener('play', handlePlay);
    element.addEventListener('pause', handlePause);
    element.addEventListener('ended', handleEnded);

    // Cleanup function
    const cleanup = () => {
      element.removeEventListener('play', handlePlay);
      element.removeEventListener('pause', handlePause);
      element.removeEventListener('ended', handleEnded);

      // Clean up any pending events for this video
      globalState.managerInitiatedEvents.forEach(eventKey => {
        if (eventKey.endsWith(`-${videoId}`)) {
          globalState.managerInitiatedEvents.delete(eventKey);
        }
      });
    };

    // Store registration
    videoRegistry.set(videoId, { cleanup, element });

    // Return cleanup function
    return () => {
      cleanup();
      videoRegistry.delete(videoId);
    };
  },

  // Simplified operation to switch to a new video
  async switchToVideo(videoId: string): Promise<void> {
    // Early return if already playing this video
    if (globalState.currentlyPlaying === videoId) {
      return;
    }

    try {
      // Step 1: Pause all videos except the target video
      await this.pauseAllVideos({ excludeVideoId: videoId });

      // Step 2: Update state
      globalState.currentlyPlaying = videoId;
      notifySubscribers();

      // Note: The actual video.play() will be handled by the user clicking
      // on the video controls, ensuring proper user gesture requirement
    } catch (error) {
      console.warn(`Failed to switch to video ${videoId}:`, error);
      // Reset state on error
      globalState.currentlyPlaying = null;
      notifySubscribers();
    }
  },
};

// React hook interface
export const useVideoManager = () => {
  const [, forceUpdate] = useState({});
  const [isClient, setIsClient] = useState(false);

  // Client-side only
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Subscribe to state changes
  const subscribe = useCallback(() => {
    const forceUpdateFn = () => forceUpdate({});
    subscribers.add(forceUpdateFn);

    return () => {
      subscribers.delete(forceUpdateFn);
    };
  }, []);

  // Hook into subscription system
  useEffect(() => {
    if (!isClient) return;

    return subscribe();
  }, [isClient, subscribe]);

  // Exposed API
  const playVideo = useCallback(
    async (videoId: string) => {
      if (!isClient) return;
      await VideoManager.switchToVideo(videoId);
    },
    [isClient]
  );

  const pauseVideo = useCallback(
    (videoId: string) => {
      if (!isClient) return;
      VideoManager.pauseVideo(videoId);
    },
    [isClient]
  );

  const registerVideo = useCallback(
    (videoId: string, element: HTMLVideoElement) => {
      if (!isClient) return () => {};
      return VideoManager.registerVideo(videoId, element);
    },
    [isClient]
  );

  const isVideoPlaying = useCallback(
    (videoId: string) => {
      if (!isClient) return false;
      return VideoManager.isVideoPlaying(videoId);
    },
    [isClient]
  );

  const getCurrentlyPlaying = useCallback(() => {
    if (!isClient) return null;
    return VideoManager.getCurrentlyPlaying();
  }, [isClient]);

  return {
    getCurrentlyPlaying,
    isVideoPlaying,
    pauseVideo,
    playVideo,
    registerVideo,
  };
};
